import { useState } from "react";
import { Loader2, AlertTriangle, Sparkles, Lock } from "lucide-react";

const FROZEN_FIELDS = [
  { label: "Skill Focus", helper: "frozen — supply mode" },
  { label: "Grade", helper: "frozen — supply mode" },
  { label: "Subject", helper: "frozen — supply mode" },
  { label: "Time", helper: "frozen — supply mode" },
  { label: "Activity Type", helper: "frozen — supply mode" },
];

/**
 * Emergency Supply Plan generator — bare-bones, supply-teacher-friendly.
 * The card visually flips to a dark theme, all dropdowns freeze, and the
 * only input the teacher can use is the prompt textarea. They type exactly
 * what each subject + length should be and the AI does the rest.
 */
export const SupplyPlanForm = ({ onGenerate, loading }) => {
  const [prompt, setPrompt] = useState("");

  const ready = prompt.trim().length > 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!ready || loading) return;
    onGenerate(prompt.trim());
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="supply-plan-form"
      className="rounded-2xl shadow-xl shadow-black/30 p-6 md:p-10 bg-black text-white border border-black"
    >
      <div className="flex items-start gap-3 mb-2">
        <span className="h-9 w-9 shrink-0 rounded-md bg-white/10 border border-white/20 grid place-items-center">
          <AlertTriangle className="h-4 w-4 text-white" />
        </span>
        <div className="min-w-0">
          <p className="font-bold uppercase text-xs tracking-[0.25em] text-white/65">
            Emergency Supply Plans
          </p>
          <h2 className="font-extrabold text-2xl sm:text-3xl text-white mt-2 tracking-tight">
            Type the day. Hit go. You&apos;re done.
          </h2>
        </div>
      </div>

      <div
        data-testid="supply-plan-callout"
        className="mt-6 rounded-lg border border-white/15 bg-white/5 px-4 py-3 text-sm text-white/85 leading-relaxed"
      >
        <p>
          <span className="font-bold uppercase tracking-[0.18em] text-[#96ded1]">
            Supply mode is on.
          </span>{" "}
          The dropdowns are locked. The prompt below is the only thing you need
          to fill in — list each subject the supply teacher should run and how
          long each one should take. The AI will generate stripped-down lesson
          blocks (no learning goals, no curriculum codes, no exit tickets) and
          will print worksheets only when they&apos;re genuinely useful.
        </p>
      </div>

      {/* Frozen field row — visible so the teacher SEES that the normal
          generator inputs are deliberately disabled. */}
      <div
        data-testid="supply-plan-frozen"
        className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-2"
        aria-hidden="true"
      >
        {FROZEN_FIELDS.map((f) => (
          <div
            key={f.label}
            className="rounded-md border border-white/10 bg-white/5 px-3 py-2.5 text-left opacity-60"
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">
              {f.label}
            </p>
            <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-white/55">
              <Lock className="h-3 w-3" /> Locked
            </p>
          </div>
        ))}
      </div>

      <div className="mt-6 space-y-2">
        <label
          htmlFor="supply-prompt"
          className="text-[#96ded1] font-bold text-xs uppercase tracking-[0.18em]"
        >
          What does the supply teacher need to run today?
        </label>
        <textarea
          id="supply-prompt"
          data-testid="supply-prompt"
          rows={6}
          autoFocus
          placeholder={
            'e.g. Grade 5. 40 min math — fractions practice. 30 min language — reading comprehension on a short story. 30 min art — leaf prints with crayons. 45 min phys ed — dodgeball review. Class has one student with an EA-supported IEP, please flag where the EA can help.'
          }
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="w-full rounded-md border border-white/15 bg-white/5 text-white font-medium px-3 py-3 placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[#96ded1] resize-none"
        />
        <p className="text-[11px] text-white/55 font-medium">
          Tip: be specific about timing — &quot;40 min math&quot; produces a
          tighter plan than &quot;some math&quot;.
        </p>
      </div>

      <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-xs text-white/55">
          Saved to your library under <span className="font-bold">Supply Plans</span>
        </p>
        <button
          type="submit"
          data-testid="generate-supply-button"
          disabled={!ready || loading}
          className="inline-flex items-center gap-2 px-7 h-12 rounded-lg text-base font-extrabold bg-[#96ded1] text-[#0f1c28] hover:bg-[#a4e7da] disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Drafting the day…
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Build supply plan
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default SupplyPlanForm;
